import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ExpenseListView } from '../components/expenses/ExpenseListView';

/**
 * Standalone expenses route. When mounted under /events/:eventId/expenses
 * it pre-scopes the list by event; otherwise shows all expenses with the
 * built-in event-filter dropdown.
 *
 * All UI (filters, summary, action buttons, modals) lives in ExpenseListView,
 * so this page is intentionally a thin shell — the same component renders on
 * the event detail page and the vendor detail page.
 */
export function ExpensesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  // `?expenseItemId=` is what `entityRoute('ExpenseItem', id)` produces, so a
  // conversation or an audit entry about one expense opens that expense rather
  // than the whole list. A link that carried an id nothing read would be a
  // promise this page did not keep.
  const [searchParams] = useSearchParams();
  const openExpenseItemId = searchParams.get('expenseItemId') ?? undefined;
  const { user: currentUser } = useAuth();

  if (!currentUser) return null;

  const isEventScoped = !!eventId;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEventScoped ? 'Event Expenses' : 'All Expenses'}
        </h1>
      </div>

      <ExpenseListView
        entityType={isEventScoped ? 'EVENT' : undefined}
        entityId={eventId}
        currentUser={currentUser}
        {...(openExpenseItemId ? { openExpenseItemId } : {})}
        // Across events the page opens on the weekly cash call: what is overdue
        // and what falls due in the next 30 days.
        {...(isEventScoped ? {} : { defaultCash: ['overdue' as const, 'due30' as const] })}
      />
    </div>
  );
}
